import { useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import {
  compressSchema,
  flattenRequest,
  flattenSchema,
  getStatusForProperty,
  status,
  transformGuidance,
  transformIncorrectTypes,
} from '../../util';
import {
  CodeSnippet,
  Content,
  CopyTextButton,
  Header,
  HeaderLabel,
  InfoCard,
  Link,
  Page,
  Progress,
  ResponseErrorPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { useLocation, useParams } from 'react-router-dom';
import { Breadcrumbs, Grid, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import CheckRounded from '@material-ui/icons/CheckRounded';
import { FailureSample, MoveAvroSchema, drEventTaxonomyReferenceDataProxyApiRef, EventsDetailedMap } from '../../api';
import { useApi } from '@backstage/core-plugin-api';
import ExpandMore from '@material-ui/icons/ExpandMore';
import Android from '@material-ui/icons/Android';
import Computer from '@material-ui/icons/Computer';
import Apple from '@material-ui/icons/Apple';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { BadResponse } from './BadResponse';
import { Accordion, AccordionSummary, AccordionDetails } from './Accordion';

const iconStyles = {
  root: {
    color: 'rgba(0, 0, 0, .58)',
    verticalAlign: 'middle',
  },
};

const StyledAndroid = withStyles(iconStyles)(Android);
const StyledWeb = withStyles(iconStyles)(Computer);
const StyledApple = withStyles(iconStyles)(Apple);

const EventDetailComponent = ({ eventDetails }: MoveAvroSchema) => {
  const { eventID } = useParams();
  const actualEventName = eventID?.replace('Event', '');
  const currentUrl = useLocation().pathname;
  const jsonAvroSchema = JSON.parse(JSON.stringify(eventDetails)) as MoveAvroSchema;
  const compressedSchema = compressSchema(jsonAvroSchema);
  const propertiesArray = jsonAvroSchema.fields?.find(field => field.name === 'properties')?.type.fields;

  const listOfParams: any[] = [];

  compressedSchema.forEach((value, key) => {
    if (!(key === 'event' || key === 'properties')) {
      const paramName = key.replace('properties.', '');
      const linkToParam = `${currentUrl}/${paramName}`;
      const matchingField = propertiesArray.find((field: { name: string }) => field.name === paramName);
      const docValue = matchingField?.doc || '';
      listOfParams.push({
        param: (
          <Link underline="always" key={paramName} to={linkToParam}>
            {paramName}
          </Link>
        ),
        description: docValue,
        name: paramName,
        required: value.isOptional ? '' : <CheckRounded fontSize="large" color="action" />,
      });
    }
  });

  const columns: TableColumn[] = [
    { title: 'Parameter Name', field: 'param' },
    { title: 'Required', field: 'required' },
    { title: 'Description', field: 'description' },
  ];
  return (
    <Table
      title={`Parameters for "${actualEventName}" event`}
      subtitle={jsonAvroSchema.fields?.at(0)?.doc}
      options={{ search: false, paging: false, sorting: true, tableLayout: 'auto' }}
      columns={columns}
      data={listOfParams.sort((a, b) => (a.name > b.name ? 1 : -1))}
    />
  );
};

const EventExampleComponent = ({ eventDetails }: MoveAvroSchema) => {
  const { eventID } = useParams();
  const actualEventName = eventID?.replace('Event', '');
  const jsonAvroSchema = JSON.parse(JSON.stringify(eventDetails)) as MoveAvroSchema;
  const compressedSchema = compressSchema(jsonAvroSchema);
  const exampleObject: any = {};
  compressedSchema.forEach((value, key) => {
    if (!(key === 'event' || key === 'properties')) {
      const paramName = key.replace('properties.', '');
      exampleObject[paramName] = value.examples ? value.examples?.at(0) : '...';
    }
  });
  const dynamicFieldString = JSON.stringify(exampleObject, null, 6);
  const finalObject =
    actualEventName === 'pageView'
      ? `.trackPage(
    "${actualEventName}",${dynamicFieldString})`
      : `.trackEvent(
    "${actualEventName}",${dynamicFieldString})`;
  const containerStyle = { width: 1500 };
  return (
    <InfoCard>
      <Typography variant="h5">Example Implementation</Typography>
      <div style={containerStyle}>
        <CodeSnippet text={finalObject} language="json" showCopyCodeButton />
      </div>
    </InfoCard>
  );
};

type EventSample = {
  value: any;
  property: string;
  status: string;
  reason: string | null;
};

export const topicMapping: any = {
  'prism.clickstream.DirectSendAndroidEvent': <StyledAndroid />,
  'prism.clickstream.DirectSendIosEvent': <StyledApple />,
  'prism.clickstream.DirectSendWebEvent': <StyledWeb />,
};
const getStyledTopicIcon = (topic: string) => {
  return topicMapping[topic] || <StyledWeb />;
};

const EventFailureSample = ({ flattenedSchema, sample, expanded, sampleNumber }: any) => {
  const flattenedRequest = flattenRequest(sample.request);
  const guidanceMap = transformGuidance(sample.response.guidance);
  const wrongTypes = transformIncorrectTypes(sample.response.incorrectTypes);
  const topic = sample.topic || 'prism.clickstream.DirectSendWebEvent';

  const title: string = `${sample.response.event} (${sample.timestamp})`;

  const keySet = new Set(Object.keys(flattenedRequest));
  sample.response.missingFields.forEach((missingField: string) => {
    keySet.add(missingField);
  });

  const keys = [...keySet].sort((a, b) => a.localeCompare(b));
  const colorMap: any = {
    PASS: '#cfc',
    FAIL: '#fbb',
  };

  let snowflakeQuery: string = `SELECT * FROM PRISM_RAW.KAFKA_FLATTEN.CLICKSTREAM_WEBEVENT
  WHERE ${sample.request.event === null ? "TYPE = 'page'" : `TYPE = 'track' AND EVENT = '${sample.request.event}'`}`;

  const results: EventSample[] = [];
  keys.forEach((k: string) => {
    const val = flattenedRequest[k];
    const statusOfElement = getStatusForProperty(flattenedSchema[k], guidanceMap[k], wrongTypes[k], val);
    if (statusOfElement.status === status.UNRECOGNIZED) {
      return;
    }

    let value = '';
    if (typeof val !== 'object') {
      if (val === '') {
        value = '"" (empty string)';
      } else {
        value = val;
      }
    } else if (val === null) {
      value = 'null';
    } else {
      value = '(Object)';
    }

    if (statusOfElement.status === status.FAIL) {
      let v: any = val;
      if (typeof val === 'string') {
        v = `'${val.replaceAll('\\', '\\\\').replaceAll("'", "\\'")}'`;
      }

      snowflakeQuery += ` AND JSON_EXTRACT_PATH_TEXT(PROPERTIES, '${k.split('.').reverse()[0]}') = ${v}`;
    }

    results.push({
      value,
      property: k,
      ...statusOfElement,
    });
  });

  snowflakeQuery += ` ORDER BY _kafka_createtime DESC LIMIT 10`;

  const icon = getStyledTopicIcon(topic);

  return (
    <Accordion square defaultExpanded={expanded === true}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls={`panel${sampleNumber}-content`}
        id={`panel${sampleNumber}-header`}
      >
        <span style={{ verticalAlign: 'middle' }}>
          {icon}
          <span style={{ marginLeft: '10px' }}>{title}</span>
          &nbsp;| Copy Request:{' '}
          <CopyTextButton text={JSON.stringify(sample.request)} tooltipText="Request copied to clipboard" />
          &nbsp;| Copy Snowflake Query:{' '}
          <CopyTextButton text={snowflakeQuery} tooltipText="Snowflake query copied to clipboard" />
        </span>
      </AccordionSummary>
      <AccordionDetails>
        {results.map((item: EventSample) => {
          return (
            <div
              style={{
                background: colorMap[item.status],
                flexBasis: '100%',
                margin: '0 0 3 0',
                padding: 10,
              }}
            >
              <b style={{ display: 'block' }}>{item.property}</b>
              <span
                style={{
                  display: 'inline-block',
                  fontStyle: 'italic',
                  maxWidth: '750px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  marginRight: 10,
                  padding: 5,
                  verticalAlign: 'top',
                }}
              >
                {item.value}
              </span>
              <span style={{ display: 'inline-block', verticalAlign: 'top', padding: 5 }}>{item.reason}</span>
            </div>
          );
        })}
      </AccordionDetails>
    </Accordion>
  );
};

const EventFailureSampleComponent = ({ schema, samples }: any) => {
  const flattenedSchema = flattenSchema(schema as MoveAvroSchema);
  const topicKeys = Object.keys(topicMapping);
  const [visibleTopics, setVisibleTopics] = useState<string[]>(topicKeys);
  const updateVisibleTopics = (_: React.MouseEvent<HTMLElement>, topicFilter: string[]) => {
    setVisibleTopics(topicFilter);
  };

  let count = 0;

  return (
    <InfoCard>
      <Typography variant="h5">
        <span style={{ marginRight: '10px' }}>Failure Samples</span>
        <ToggleButtonGroup onChange={updateVisibleTopics} value={visibleTopics}>
          {topicKeys.map((topic: string) => {
            return (
              <ToggleButton key={topic} value={topic} aria-label={topic}>
                {getStyledTopicIcon(topic)}
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>
      </Typography>
      <Typography variant="subtitle1">
        Displaying the last 10 failure samples across Android, iOS, and Web within the last ~10 minutes. Use the filters
        above to view client specific failures.
      </Typography>
      {samples?.length === 0 ? 'No failures found.' : ''}
      {(samples as FailureSample[]).map((sample: FailureSample, index): any => {
        if (!visibleTopics.includes(sample.topic)) {
          return undefined;
        }

        count++;
        return <EventFailureSample flattenedSchema={flattenedSchema} sample={sample} sampleNumber={index + 1} />;
      })}
      {count === 0 && samples?.length !== 0 ? 'No failure samples matched the filters.' : ''}
    </InfoCard>
  );
};

export const EventComponent = ({ mockData }: any) => {
  const { eventID } = useParams();
  const apiClient = useApi(drEventTaxonomyReferenceDataProxyApiRef);
  const actualEventName = eventID?.replace('Event', '');
  const { value, loading, error } = useAsync(async () => {
    if (mockData !== undefined) {
      return mockData.data;
    }
    return apiClient.get<EventsDetailedMap>(`digital-analytics-tracking-validator/v1/event/${eventID}`);
  });

  if (loading) {
    return <Progress />;
  } else if (error) {
    return <ResponseErrorPanel error={error} />;
  } else if (value instanceof Response) {
    return <BadResponse response={value} />;
  }

  const eventDetails = value !== undefined ? value[actualEventName as string] : undefined;

  return (
    <Page themeId="tool">
      <Header
        title="Event Taxonomy Reference Data (ETRD)"
        subtitle="Streamline feature tracking with event taxonomy-related guidance"
      >
        <HeaderLabel label="Owner" value="Data Reliability" />
        <HeaderLabel label="Slack" value="#data-reliability" />
      </Header>
      <Content>
        <Grid direction="column" spacing={3} container>
          <Grid item>
            <Breadcrumbs>
              <Link to="/dr-event-taxonomy-reference-data">Home</Link>
              <Link to="/dr-event-taxonomy-reference-data/events">Events</Link>
              <Link to={`/dr-event-taxonomy-reference-data/events/${actualEventName}`}>{actualEventName}</Link>
            </Breadcrumbs>
          </Grid>
          <Grid item>
            {eventDetails !== undefined ? (
              <EventDetailComponent testID="EventDetail" eventDetails={eventDetails} />
            ) : (
              <Typography variant="h6">Event not found in the schema registry</Typography>
            )}
          </Grid>
          <Grid item>{eventDetails !== undefined ? <EventExampleComponent eventDetails={eventDetails} /> : ''}</Grid>
          <Grid item>
            <EventFailureSampleComponent schema={eventDetails} samples={value.failureSampling || []} />
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};
